import SEOHead, {
  getWebPageSchema,
  getBreadcrumbSchema,
  getOrganizationSchema,
} from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo, testimonials } from '@/mocks/comics';

export default function AboutPage() {
  return (
    <>
      <SEOHead
        title={`关于我们 - ${siteInfo.name}`}
        description={`了解吹雪漫画网 - ${siteInfo.description.substring(0, 120)}`}
        keywords="吹雪漫画网,关于我们,漫画网站,免费漫画,品牌介绍"
        canonicalUrl="https://chuixue.manhua.com/about"
        schemaData={{
          ...getWebPageSchema(
            `关于我们 - ${siteInfo.name}`,
            siteInfo.description,
            'https://chuixue.manhua.com/about',
          ),
          ...getBreadcrumbSchema([
            { name: '首页', url: 'https://chuixue.manhua.com/' },
            { name: '关于我们', url: 'https://chuixue.manhua.com/about' },
          ]),
          ...getOrganizationSchema(),
        }}
      />

      <Breadcrumb
        items={[
          { name: '首页', href: '/' },
          { name: '关于我们' },
        ]}
      />

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-6">
        <section className="mb-14">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-heading text-foreground-900 mb-3">
              关于{siteInfo.name}
            </h1>
            <p className="text-foreground-500 text-sm max-w-2xl mx-auto">
              {siteInfo.slogan}
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="prose-custom">
              <h2 className="text-xl font-heading text-foreground-900 mb-4">品牌故事</h2>
              <p className="text-foreground-600 leading-relaxed mb-4 text-sm">
                {siteInfo.name}成立于{siteInfo.founded}，是国内领先的漫画在线阅读平台。我们致力于为广大漫画爱好者提供高品质、多元化的漫画阅读体验。从创立之初，我们就秉持着"让好漫画触手可及"的理念，不断丰富漫画资源库，优化阅读体验。
              </p>
              <p className="text-foreground-600 leading-relaxed mb-4 text-sm">
                经过多年的发展，{siteInfo.name}已经积累了超过{siteInfo.comicCount}部优质漫画作品，内容涵盖国产漫画、热血、恋爱、玄幻、武侠、科幻、悬疑、搞笑、奇幻、都市等多种类型，满足不同读者的喜好和需求。
              </p>
              <p className="text-foreground-600 leading-relaxed text-sm">
                我们采用先进的下拉式阅读技术，让读者在阅读漫画时无需频繁翻页，只需向下滑动即可流畅观看，大大提升了阅读的沉浸感和舒适度。同时，我们坚持提供无删减完整版漫画，确保读者能够欣赏到原汁原味的漫画内容。
              </p>
            </div>
          </div>
        </section>

        <section className="mb-14 bg-background-100/70 rounded-2xl p-8 md:p-10">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
              平台数据
            </h2>
            <p className="text-foreground-500 text-sm">用数字说话，见证我们的成长</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: siteInfo.comicCount, label: '漫画作品', icon: 'ri-book-open-line' },
              { value: siteInfo.userCount, label: '注册用户', icon: 'ri-user-line' },
              { value: siteInfo.dailyActive, label: '日活跃用户', icon: 'ri-fire-line' },
              { value: '99.9%', label: '服务可用率', icon: 'ri-shield-check-line' },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100">
                  <i className={`${stat.icon} text-xl text-primary-500`}></i>
                </div>
                <div className="text-2xl md:text-3xl font-heading text-foreground-900 mb-1">
                  {stat.value}
                </div>
                <div className="text-xs text-foreground-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-14">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
              我们的优势
            </h2>
            <p className="text-foreground-500 text-sm">为什么选择吹雪漫画网？</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              {
                title: '海量漫画资源',
                desc: '超过50,000部优质漫画，涵盖多种类型，每日持续更新，满足您的一切阅读需求。',
                icon: 'ri-stack-line',
              },
              {
                title: '无删减完整版',
                desc: '所有漫画均提供无删减完整版本，保证内容原汁原味，让您享受最纯粹的阅读体验。',
                icon: 'ri-file-check-line',
              },
              {
                title: '下拉式流畅阅读',
                desc: '采用下拉式阅读设计，无需翻页，一滑到底，带来沉浸式的漫画观看体验。',
                icon: 'ri-scroll-to-bottom-line',
              },
              {
                title: '高清画质',
                desc: '所有漫画均提供高清画质，每一处细节都清晰可见，让漫画人物栩栩如生。',
                icon: 'ri-hd-line',
              },
              {
                title: '完全免费',
                desc: '吹雪漫画网所有漫画完全免费阅读，无需付费订阅，让好漫画真正触手可及。',
                icon: 'ri-money-cny-circle-line',
              },
              {
                title: '多端适配',
                desc: '完美适配电脑、平板和手机，随时随地享受流畅的漫画阅读体验。',
                icon: 'ri-device-line',
              },
            ].map((advantage, index) => (
              <div key={index} className="p-5 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 mb-3">
                  <i className={`${advantage.icon} text-lg text-primary-500`}></i>
                </div>
                <h3 className="text-base font-heading text-foreground-900 mb-2">{advantage.title}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">{advantage.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-14">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
              用户口碑
            </h2>
            <p className="text-foreground-500 text-sm">听听漫友们怎么说</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="p-5 rounded-xl bg-background-50 border border-background-200/70">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 shrink-0">
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-800">{testimonial.name}</div>
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: testimonial.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-xs text-accent-500"></i>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-foreground-600 leading-relaxed">{testimonial.content}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
              联系我们
            </h2>
            <p className="text-foreground-500 text-sm">有任何问题或建议？欢迎联系我们</p>
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="p-5 rounded-xl bg-background-50 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-primary-100">
                  <i className="ri-mail-line text-lg text-primary-500"></i>
                </div>
                <div className="text-xs text-foreground-500 mb-1">电子邮箱</div>
                <div className="text-sm font-medium text-foreground-800">{siteInfo.email}</div>
              </div>
              <div className="p-5 rounded-xl bg-background-50 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-primary-100">
                  <i className="ri-wechat-line text-lg text-primary-500"></i>
                </div>
                <div className="text-xs text-foreground-500 mb-1">微信公众号</div>
                <div className="text-sm font-medium text-foreground-800">{siteInfo.wechat}</div>
              </div>
              <div className="p-5 rounded-xl bg-background-50 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-primary-100">
                  <i className="ri-qq-line text-lg text-primary-500"></i>
                </div>
                <div className="text-xs text-foreground-500 mb-1">QQ交流群</div>
                <div className="text-sm font-medium text-foreground-800">{siteInfo.qq}</div>
              </div>
            </div>

            <div className="p-6 rounded-xl bg-background-100/70">
              <h3 className="text-base font-heading text-foreground-900 mb-4 text-center">发送消息</h3>
              <form
                className="space-y-4"
                data-readdy-form=""
                id="contact-form"
                action="https://readdy.ai/api/form/d9pbq8gsenbniolessf0"
                method="POST"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
                  if (honeypot) {
                    alert('消息已发送，感谢您的反馈！');
                    form.reset();
                    return;
                  }
                  try {
                    const formData = new FormData(form);
                    formData.delete('website_alt');
                    const res = await fetch(form.action, {
                      method: 'POST',
                      body: new URLSearchParams(formData as unknown as Record<string, string>),
                      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    });
                    const text = await res.text();
                    let parsed;
                    try { parsed = JSON.parse(text); } catch { parsed = null; }
                    if (res.ok && parsed?.code === 'OK') {
                      alert('消息已发送，感谢您的反馈！');
                      form.reset();
                    } else {
                      const msg = parsed?.meta?.message || parsed?.message || '发送失败，请稍后重试';
                      alert(msg);
                    }
                  } catch {
                    alert('网络错误，请稍后重试');
                  }
                }}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-xs text-foreground-600 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs text-foreground-600 mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-xs text-foreground-600 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-xs text-foreground-600 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    placeholder="请输入您的留言内容（500字以内）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                </div>
                <div className="form-honeypot">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <button
                  type="submit"
                  className="w-full px-6 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  发送消息
                </button>
              </form>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}